import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, LockKey, CheckCircle, Circle } from "@phosphor-icons/react";
import Seo from "@/components/Seo";

export const PASSWORD_RULES = [
  { id: "len", label: "At least 12 characters", test: (p) => p.length >= 12 },
  { id: "upper", label: "An uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { id: "lower", label: "A lowercase letter", test: (p) => /[a-z]/.test(p) },
  { id: "digit", label: "A number", test: (p) => /\d/.test(p) },
  { id: "symbol", label: "A symbol", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export function PasswordRules({ value }) {
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-2" data-testid="password-rules">
      {PASSWORD_RULES.map((r) => {
        const ok = r.test(value || "");
        return (
          <li key={r.id} className={`flex items-center gap-2 font-mono text-[11px] ${ok ? "text-[#FFD300]" : "text-neutral-500"}`} data-testid={`pw-rule-${r.id}`}>
            {ok ? <CheckCircle size={13} weight="fill" /> : <Circle size={13} />} {r.label}
          </li>
        );
      })}
    </ul>
  );
}

export default function ChangePassword() {
  const { user, loading, changePassword } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return <div className="container-x py-32 font-mono text-sm">Loading...</div>;
  if (!user) { navigate("/login?next=/change-password"); return null; }

  const forced = !!user.must_change_password;
  const allRulesOk = PASSWORD_RULES.every((r) => r.test(form.next));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next !== form.confirm) { setError("New passwords do not match."); return; }
    if (!allRulesOk) { setError("New password does not meet the requirements."); return; }
    setBusy(true);
    try {
      await changePassword(form.current, form.next);
      navigate(user.role === "admin" || user.role === "instructor" ? "/admin" : "/dashboard");
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : "Unable to change password.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[88vh] flex items-center" data-testid="change-password-page">
      <Seo title="Change Password" path="/change-password" noindex />
      <div className="container-x max-w-xl">
        <div className="overline mb-4 flex items-center gap-2"><LockKey size={14} weight="duotone" className="text-[#FFD300]" /> // ACCOUNT SECURITY</div>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tighter mb-3">{forced ? "Set a new password." : "Change your password."}</h1>
        {forced && (
          <p className="text-neutral-300 mb-8 border-l-2 border-[#FFD300] pl-4 text-sm leading-relaxed" data-testid="forced-change-notice">
            Your account is using a temporary bootstrap password. For security, you must choose a new one before accessing the portal.
          </p>
        )}
        {!forced && <p className="text-neutral-400 mb-8">All other sessions are signed out once your password changes.</p>}
        <form onSubmit={submit} className="space-y-5">
          <div>
            <div className="label-mono mb-2">Current password</div>
            <input data-testid="cp-current" className="field" type="password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} required autoComplete="current-password" />
          </div>
          <div>
            <div className="label-mono mb-2">New password</div>
            <input data-testid="cp-new" className="field" type="password" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} required autoComplete="new-password" minLength={12} />
            <PasswordRules value={form.next} />
          </div>
          <div>
            <div className="label-mono mb-2">Confirm new password</div>
            <input data-testid="cp-confirm" className="field" type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required autoComplete="new-password" />
          </div>
          {error && <div data-testid="cp-error" className="font-mono text-sm text-red-400">{error}</div>}
          <button disabled={busy} type="submit" data-testid="cp-submit" className="btn-primary w-full justify-center">
            {busy ? "Updating..." : "Update password"} <ArrowRight size={14} />
          </button>
        </form>
      </div>
    </div>
  );
}
